import { ActionIcon, Button, Group, Loader, Modal, Stack, Text, Tooltip } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Redo2, RotateCcw, Save, Undo2, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { getListLayerEditsQueryKey, useListLayerEdits } from "../../../api/generated/endpoints/generation/generation";
import { FALLBACK_REFETCH_MS, canChange } from "../../../platform/jobs/refetch";
import { useImageUpload } from "../../../platform/media/image-upload";
import type { PickerLibrary } from "../../../platform/media/ImagePicker";
import { changed, committed, opened, redone, reset, undone, type History } from "./history";
import { documentKey, hasAddedPictures, readDraft, refreshAdded, writeDraft, type Draft } from "./draft";
import { LayerPickers, type PictureSource } from "./LayerPickers";
import { LayersRail } from "./LayersRail";
import { LayerStage } from "./LayerStage";
import { done, problem, wordsOf, type Notice } from "./errors";
import { NOT_SAVED, SAVED, flatten, layeredName } from "./flatten";
import { EDIT_APPLIED, EDIT_FAILED, busyLayers, editsShown, landEdit, settleEdits, type EditLanding, type EditResult } from "./edits";
import { EditLayerModal } from "./EditLayerModal";
import { readLandings, writeLandings, type Landings } from "./landings";
import {
  added,
  compositionOf,
  updated,
  visible,
  type Composition,
  type LayerDocument,
  type PictureLayer,
  type TextLayer,
} from "./model";
import { failureWords, pictureLayer, pictureReader, sizeOf, type PictureChoice } from "./picture";
import { TEXT_NOT_ADDED, TEXT_NOT_UPDATED, defaultText, nextTextName, textLayer, type TextValues } from "./text";
import classes from "./layer-editor.module.css";

// Live's words (spec G13b).
export const LOADING_LAYERS = "Loading layers";
export const LAYERS_NOT_LOADED = "The layers of this image could not be loaded.";
export const STAGE_HINT =
  "Drag a layer to move it. Drag a corner to resize it with its aspect ratio locked. Arrow keys nudge the selected layer.";

export interface LayerEditorProps {
  /** The Layerize the layers are of, and who edits them: the draft is kept per viewer and job. */
  jobId: string;
  viewer: string | null;
  /** The job's brand: whose products, generations and library "Add layer" offers. */
  library: PickerLibrary;
  /** Whether the catalog offers `image.layer_edit` to the team: "Edit with AI" works, else it is
   * Coming soon, as the Studio's tools are (G13b decision 2). */
  aiEdits: boolean;
  /** The Layerize's layer document, once read; undefined while it loads or when it failed. */
  document: LayerDocument | undefined;
  failed: boolean;
  onRetry: () => void;
  onClose: () => void;
}

/**
 * Live's layer editor (spec G13b; live's `layer-editor/**`): a full-screen dialog whose title is
 * for screen readers, never closed by a click outside, closed at once by Escape or its x. While
 * the document loads it says live's "Loading layers"; when it cannot be read, live's words and
 * "Retry". Then the toolbar and the canvas. Escape in a picker closes the picker alone, where
 * live's could close the whole editor (G13b decision 7).
 */
export function LayerEditor(props: LayerEditorProps) {
  const { jobId, viewer, library, aiEdits, document, failed, onRetry, onClose } = props;
  const [picking, setPicking] = useState(false);
  // A Layerize past its outputs' 30 days (G5) keeps its document with no pictures left: live's
  // failure words, as for a document that cannot be read.
  const shown = document?.layers.some((layer) => layer.url !== null) === true ? document : undefined;
  return (
    <Modal
      opened
      onClose={onClose}
      closeOnEscape={!picking}
      fullScreen
      withCloseButton={false}
      closeOnClickOutside={false}
      title={<span className={classes.hidden}>Edit layers</span>}
      classNames={{ body: classes.body, header: classes.hiddenHeader }}
    >
      {shown !== undefined ? (
        // A document opens afresh: its own draft read and its own history.
        <Opened
          key={JSON.stringify(shown)}
          jobId={jobId}
          viewer={viewer}
          library={library}
          aiEdits={aiEdits}
          document={shown}
          onPicking={setPicking}
          onClose={onClose}
        />
      ) : (
        // Not read yet, unreadable, or past its 30 days with no pictures left.
        <NotReady failed={failed || document !== undefined} onRetry={onRetry} onClose={onClose} />
      )}
    </Modal>
  );
}

/** Live's "Loading layers", or its words and "Retry" when the layers could not be read. */
function NotReady({ failed, onRetry, onClose }: { failed: boolean; onRetry: () => void; onClose: () => void }) {
  return (
    <Stack align="center" justify="center" className={classes.state}>
      <CloseButton onClose={onClose} />
      {failed ? (
        <>
          <Text>{LAYERS_NOT_LOADED}</Text>
          <Button variant="light" onClick={onRetry}>
            Retry
          </Button>
        </>
      ) : (
        <Loader aria-label={LOADING_LAYERS} />
      )}
    </Stack>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <Tooltip label="Close editor">
      <ActionIcon
        variant="subtle"
        aria-label="Close layer editor"
        onClick={onClose}
        data-autofocus
        className={classes.close}
      >
        <X size={20} />
      </ActionIcon>
    </Tooltip>
  );
}

/** A toolbar button: its tooltip is its label, as live's. */
function Tool(props: { label: string; icon: ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <Tooltip label={props.label}>
      <ActionIcon variant="subtle" aria-label={props.label} disabled={props.disabled} onClick={props.onClick}>
        {props.icon}
      </ActionIcon>
    </Tooltip>
  );
}

interface WorkspaceProps {
  jobId: string;
  viewer: string | null;
  library: PickerLibrary;
  aiEdits: boolean;
  document: LayerDocument;
  /** Whether a picker is open over the editor, which then leaves Escape to it. */
  onPicking: (picking: boolean) => void;
  onClose: () => void;
}

/**
 * The draft kept for this viewer and job, else the document as it is (live's restore). A draft's
 * added pictures are signed too and expire, so they are read again from their refs before the
 * editor opens, while live's "Loading layers" stays up; one that is gone is left out. A read that
 * fails otherwise shows live's words and "Retry", so a passing outage never drops a layer.
 */
function Opened(props: WorkspaceProps) {
  const { jobId, viewer, library, document, onClose } = props;
  const [kept] = useState(() => (viewer === null ? null : readDraft(viewer, jobId, document)));
  const rereading = kept !== null && hasAddedPictures(kept);
  const reread = useQuery({
    queryKey: ["layer-editor-draft", viewer, jobId, documentKey(document)],
    queryFn: () => refreshAdded(kept!, pictureReader(library)),
    enabled: rereading,
    retry: false,
    // Read once per opening: a return to the tab does not read every added picture again.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
  });
  if (rereading && reread.data === undefined) {
    return <NotReady failed={reread.isError} onRetry={() => void reread.refetch()} onClose={onClose} />;
  }
  return <Workspace {...props} restored={rereading ? reread.data! : kept} />;
}

function Workspace({
  jobId,
  viewer,
  library,
  aiEdits,
  document,
  onPicking,
  onClose,
  restored,
}: WorkspaceProps & { restored: Draft | null }) {
  const [history, setHistory] = useState<History>(() => {
    const origin = opened(compositionOf(document, jobId));
    return restored === null ? origin : { ...origin, present: restored.composition };
  });
  // A drag's or a resize's composition while the pointer moves.
  const [during, setDuring] = useState<Composition | null>(null);
  const [selected, setSelected] = useState<string | null>(
    () => restored?.selected ?? visible(history.present).at(-1) ?? null,
  );
  const root = useRef<HTMLDivElement>(null);
  // Kept after each change and each selection, never mid-gesture, as live's.
  useEffect(() => {
    if (viewer !== null) {
      writeDraft(viewer, jobId, document, { composition: history.present, selected });
    }
  }, [viewer, jobId, document, history.present, selected]);
  // A row of the rail chosen: the layer's button on the canvas takes the focus, so the arrows
  // nudge it (G13b decision 7).
  const chooseFromRail = (id: string) => {
    setSelected(id);
    requestAnimationFrame(() =>
      root.current?.querySelector<HTMLElement>(`[data-layer="${CSS.escape(id)}"]`)?.focus(),
    );
  };
  const commit = (next: Composition) => {
    setHistory((current) => committed(current, next));
    // A removed layer hands the selection to the top one left, as live's.
    if (selected !== null && next.transforms[selected]?.removed) {
      setSelected(visible(next).at(-1) ?? null);
    }
  };
  // Live's "Add layer": the picker open, a picture on its way, and what last failed.
  const [picker, setPicker] = useState<PictureSource | null>(null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Live's "Edit layer with AI" open on a picture layer.
  const [editing, setEditing] = useState<PictureLayer | null>(null);
  useEffect(() => onPicking(picker !== null || editing !== null), [picker, editing, onPicking]);
  const pick = (source: PictureSource) => {
    setNotice(null);
    setPicker(source);
  };
  // Live's new text: "Add text" centred on top, selected; one undo step.
  const addText = async () => {
    setNotice(null);
    setAdding(true);
    try {
      const layer = await textLayer(crypto.randomUUID(), nextTextName(history.present.layers), defaultText(history.present), history.present);
      setHistory((current) => committed(current, added(current.present, layer)));
      setSelected(layer.id);
    } catch (error) {
      setNotice(problem(wordsOf(error, TEXT_NOT_ADDED)));
    } finally {
      setAdding(false);
    }
  };
  // A text edit: measured again and kept centred on its frame, one undo step. Typing outruns the
  // font and the measuring, so the last edit wins, as live's, and the controls show what was last
  // asked for meanwhile; an undo drops an edit still on its way.
  const edits = useRef(0);
  const [pendingText, setPendingText] = useState<{ id: string; values: TextValues } | null>(null);
  const changeText = async (layer: TextLayer, values: TextValues) => {
    const edit = ++edits.current;
    setPendingText({ id: layer.id, values });
    const { frame } = layer;
    try {
      const next = await textLayer(layer.id, layer.name, values, history.present, {
        x: frame.x + frame.width / 2,
        y: frame.y + frame.height / 2,
      });
      if (edit !== edits.current) return;
      setPendingText(null);
      setNotice(null);
      setHistory((current) => committed(current, updated(current.present, next)));
    } catch (error) {
      if (edit !== edits.current) return;
      setPendingText(null);
      setNotice(problem(wordsOf(error, TEXT_NOT_UPDATED)));
    }
  };
  // Live's "Save to Library": the visible layers as one PNG, uploaded to the brand's library as a
  // picture. Nothing changes while it runs (a selection may); "Saved" holds for the composition
  // saved, so any change clears it and an undo back to it shows it again.
  const uploading = useRef<AbortController | null>(null);
  const exporting = useRef<AbortController | null>(null);
  const upload = useImageUpload({
    teamId: library.teamId,
    brandId: library.brandId,
    onUploaded: () => undefined,
    onUploading: (controller) => {
      uploading.current = controller;
    },
  });
  useEffect(
    () => () => {
      exporting.current?.abort();
      uploading.current?.abort();
    },
    [],
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Composition | null>(null);
  const locked = saving || upload.pending;
  const save = async () => {
    const target = history.present;
    setNotice(null);
    setSaving(true);
    const controller = new AbortController();
    exporting.current = controller;
    try {
      const png = await flatten(target, controller.signal);
      const file = new File([png], layeredName(new Date()), { type: "image/png" });
      if (await upload.send(file)) {
        setSavedAt(target);
        setNotice(done(SAVED));
      }
    } catch (error) {
      if (!controller.signal.aborted) setNotice(problem(wordsOf(error, NOT_SAVED)));
    } finally {
      setSaving(false);
    }
  };
  // Live's AI edits: the server's list of this Layerize's edits, read again while one runs (the
  // stream reads it again on each move), and where each started here is to land.
  const layerEdits = useListLayerEdits(library.teamId, jobId, {
    query: {
      // None to read where the team may not edit with AI, as live's.
      enabled: aiEdits,
      refetchInterval: (query) =>
        (query.state.data?.items ?? []).some((job) => canChange(job.status)) ? FALLBACK_REFETCH_MS : false,
    },
  });
  const [landings, setLandings] = useState<Landings>(() => readLandings(viewer, jobId));
  // Kept for this viewer and Layerize, so an edit that finishes while the editor is closed lands
  // when it opens again.
  useEffect(() => writeLandings(viewer, jobId, landings), [viewer, jobId, landings]);
  const editJobs = layerEdits.data?.items ?? [];
  // Each new read of the list: a settled edit started here lands as it asked, once, and a failed
  // one says so once (live's). Done while rendering, as the read is what changed; it waits while a
  // drag, a text change or a save is under way, which would otherwise write over it.
  const [read, setRead] = useState(layerEdits.data);
  if (layerEdits.data !== read && during === null && pendingText === null && !locked) {
    setRead(layerEdits.data);
    const outcome = settleEdits(history, editJobs, landings);
    if (outcome.settled.length > 0) {
      setLandings(Object.fromEntries(Object.entries(landings).filter(([id]) => !outcome.settled.includes(id))));
      if (outcome.landed) {
        setHistory(outcome.history);
        if (outcome.selected !== null) setSelected(outcome.selected);
        setNotice(done(EDIT_APPLIED));
      }
      if (outcome.failed !== undefined) setNotice(problem(outcome.failed.error?.message ?? EDIT_FAILED));
    }
  }
  const queryClient = useQueryClient();
  const startedEdit = (editJob: { id: string }, landing: EditLanding) => {
    setLandings((current) => ({ ...current, [editJob.id]: landing }));
    setEditing(null);
    // The list shows it running at once; the stream reads it again as it moves.
    void queryClient.invalidateQueries({ queryKey: getListLayerEditsQueryKey(library.teamId, jobId) });
  };
  // Live's "Add as layer": a finished edit not in the composition, centred on top and selected.
  const addEdit = (result: EditResult) => {
    const landing = landEdit(history.present, result, null);
    setHistory((current) => committed(current, landing.composition));
    setSelected(landing.layer);
  };
  const travel = (move: (current: History) => History) => {
    edits.current += 1;
    setPendingText(null);
    setHistory(move);
  };
  // The chosen picture on top, centred, and selected, as live's; one undo step.
  const addPicture = async (choice: PictureChoice) => {
    setPicker(null);
    setAdding(true);
    try {
      const size = await sizeOf(choice);
      const layer = pictureLayer(crypto.randomUUID(), choice, size, history.present);
      setHistory((current) => committed(current, added(current.present, layer)));
      setSelected(layer.id);
    } catch (error) {
      setNotice(problem(failureWords(error)));
    } finally {
      setAdding(false);
    }
  };
  const composition = during ?? history.present;
  return (
    <div className={classes.workspace} ref={root}>
      <Group justify="space-between" wrap="nowrap" className={classes.toolbar}>
        <Group gap="sm" wrap="nowrap">
          <CloseButton onClose={onClose} />
          <div>
            <Text fw={500}>Edit layers</Text>
            <Text size="xs" c="var(--cai-color-text-muted)">
              {Math.round(composition.width)} × {Math.round(composition.height)}
            </Text>
          </div>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Tool label="Undo" icon={<Undo2 size={18} />} disabled={locked || history.past.length === 0} onClick={() => travel(undone)} />
          <Tool label="Redo" icon={<Redo2 size={18} />} disabled={locked || history.future.length === 0} onClick={() => travel(redone)} />
          <Tool label="Reset all changes" icon={<RotateCcw size={18} />} disabled={locked || !changed(history)} onClick={() => travel(reset)} />
          <Button
            size="xs"
            leftSection={history.present === savedAt ? <Check size={16} /> : <Save size={16} />}
            loading={locked}
            disabled={history.present === savedAt || visible(history.present).length === 0}
            onClick={() => void save()}
          >
            {history.present === savedAt ? "Saved" : "Save to Library"}
          </Button>
        </Group>
      </Group>
      <div className={classes.main}>
        <div className={classes.stageColumn}>
          <LayerStage
            composition={composition}
            selected={selected}
            locked={locked}
            onSelect={setSelected}
            onGesture={setDuring}
            onCommit={commit}
          />
          <Text size="xs" c="var(--cai-color-text-muted)" ta="center" className={classes.hint}>
            {STAGE_HINT}
          </Text>
        </div>
        <LayersRail
          composition={composition}
          selected={selected}
          adding={adding}
          locked={locked}
          notice={notice}
          onDismissNotice={() => setNotice(null)}
          upload={upload}
          onSelect={chooseFromRail}
          onCommit={commit}
          onPick={pick}
          onAddText={() => void addText()}
          pendingText={pendingText}
          aiEdits={aiEdits}
          edits={editsShown(editJobs, history.present, landings)}
          busy={busyLayers(editJobs, landings)}
          onEditWithAI={setEditing}
          onAddEdit={addEdit}
          onChangeText={(layer, values) => void changeText(layer, values)}
        />
      </div>
      <LayerPickers
        library={library}
        open={picker}
        onClose={() => setPicker(null)}
        onChoose={(choice) => void addPicture(choice)}
      />
      {editing === null ? null : (
        <EditLayerModal
          library={library}
          layerizeJobId={jobId}
          layer={editing}
          onStarted={startedEdit}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
